import { ChangeEvent, forwardRef, useState, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useReactiveVar } from '@apollo/client';
import { cx } from '@emotion/css';
import { Autocomplete as AutocompleteBase, TextField, InputAdornment } from '@mui/material';

import { IYandexMapAutocompleteProps, IPlace } from './yandex-maps-autocomplete.models';
import { useStyles } from './yandex-maps-autocomplete.styles';
import { setAutocompletedValue, setError } from '../../graphql/ecp-locator/ecp-locator.cache';
import { IconTypes, Icon } from '../common';
import { EcpErrors } from '../../constants/ecp-locator/error-types';
import { throttle } from '../../utils/throttle';

export const YandexMapAutocomplete = forwardRef<HTMLDivElement, IYandexMapAutocompleteProps>(
  (
    {
      fieldName,
      fullWidth,
      rootStyles,
      inputStyles,
      rootInputStyles,
      onChildInputChange,
      onAutocompletedSearch,
      onAutocompletedValue,
      onAutocompletedUri,
      isOpenPredictions,
      onFormSubmitted,
      variant = 'standard',
      startIcon,
      helperText,
      inputError,
      endAdornmentStyles,
    },
    ref,
  ): JSX.Element => {
    const intl = useIntl();
    const classes = useStyles();
    const [autocompleteValue, setAutocompleteValue] = useState<string | IPlace | null>(null);
    const [options, setOptions] = useState<IPlace[]>([]);
    const autocompletedValue = useReactiveVar(setAutocompletedValue) as string;
    const searchError = useReactiveVar(setError);
    const [inputValue, setInputValue] = useState('');

    // Centralize min chars requirement for opening suggestions
    const MIN_CHARS_TO_OPEN = 3;

    const checkOpenPredictions = (): boolean =>
      inputValue.length >= MIN_CHARS_TO_OPEN && !!isOpenPredictions && !autocompleteValue;

    const fetchOptions = useMemo(
      () =>
        throttle(async (input: string, callback: (results?: IPlace[]) => void) => {
          if (!input) {
            callback([]);
            return;
          }

          try {
            const url = `https://suggest-maps.yandex.ru/v1/suggest?apikey=${
              process.env.REACT_APP_YANDEX_GEOSUGGEST_API_KEY
            }&text=${encodeURIComponent(input)}&lang=ru_RU&attrs=uri`;

            const res = await fetch(url);
            if (!res.ok) {
              callback([]);
              return;
            }

            const data = await res.json();
            const results: IPlace[] = (data?.results || []).map((item: any) => ({
              description: item.title?.text || '',
              uri: item.uri,
              structured_formatting: {
                main_text: item.title?.text || '',
                secondary_text: item.subtitle?.text || '',
                main_text_matched_substrings: [],
              },
            }));

            callback(results);
          } catch {
            callback([]);
          }
        }, 300),
      [],
    );

    const startAdornment = startIcon ? (
      <InputAdornment disablePointerEvents position="start">
        {startIcon}
      </InputAdornment>
    ) : undefined;

    useEffect(() => {
      if (!(searchError === EcpErrors.errorValue)) {
        if (autocompletedValue) {
          setInputValue(autocompletedValue);
          onChildInputChange(autocompletedValue);
        }
      }
    }, [autocompletedValue, searchError]);

    return (
      <AutocompleteBase<IPlace, false, false, true>
        data-testid="yandex-map-autocomplete"
        ref={ref}
        classes={{
          root: cx(classes.root, rootStyles, {
            [classes.fullWidth]: fullWidth,
          }),
          inputRoot: cx(rootInputStyles, {
            [classes.inputError]: inputError,
          }),
          input: inputStyles,
          clearIndicator: inputValue.length ? classes.clearIndicatorShown : undefined,
          paper: classes.paper,
          listbox: classes.list,
          endAdornment: endAdornmentStyles,
        }}
        freeSolo
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
        filterOptions={(filterOptions) => filterOptions}
        options={options}
        open={checkOpenPredictions()}
        autoComplete
        includeInputInList
        filterSelectedOptions
        selectOnFocus
        handleHomeEndKeys
        clearIcon={<Icon type={IconTypes.close} className={classes.clearIndicator} />}
        clearOnBlur={false}
        openOnFocus={false}
        forcePopupIcon={false}
        value={autocompleteValue}
        inputValue={inputValue}
        onChange={(event: any, newValue: string | IPlace | null) => {
          if (newValue && typeof newValue !== 'string') {
            setOptions([newValue, ...options]);
          }
          setAutocompleteValue(newValue);
          if (typeof newValue !== 'string' && newValue) {
            const fullAddress = [
              newValue?.structured_formatting.main_text,
              newValue?.structured_formatting.secondary_text,
            ]
              .filter(Boolean)
              .join(', ');

            onChildInputChange(fullAddress);
            if (onAutocompletedValue && onAutocompletedSearch) {
              onAutocompletedSearch(true);
              onAutocompletedValue(fullAddress);
              onAutocompletedUri?.(newValue?.uri || '');
            }
          }

        }}
        onInputChange={(event, newInputValue, reason) => {
          setError('');
          if (reason === 'clear') {
            onAutocompletedValue?.('');
            onAutocompletedUri?.('');
          }
          setInputValue(newInputValue);
          onChildInputChange(newInputValue);
          if (onFormSubmitted) {
            onFormSubmitted(false);
          }

          // Fetch suggestions only in response to user input and when min chars satisfied
          if (newInputValue && newInputValue.length >= MIN_CHARS_TO_OPEN && !!isOpenPredictions) {
            fetchOptions(newInputValue, (results?: IPlace[]) => {
              let newOptions: IPlace[] = [];
              if (autocompleteValue && typeof autocompleteValue !== 'string') {
                newOptions = [autocompleteValue];
              }
              if (results) {
                newOptions = [...newOptions, ...results];
              }
              setOptions(newOptions);
            });
          } else {
            setOptions([]);
          }
        }}
        renderInput={({ InputProps, ...restParams }) => (
          <TextField
            {...restParams}
            variant={variant}
            InputProps={{
              ...InputProps,
              startAdornment,
            }}
            FormHelperTextProps={{
              classes: {
                root: classes.helperText,
              },
            }}
            name={fieldName}
            helperText={helperText}
            error={inputError}
            placeholder={intl.formatMessage({ id: 'ecp.search.placeholder' })}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              onChildInputChange(event.target.value);
              if (onAutocompletedValue) {
                onAutocompletedValue(event.target.value);
              }
            }}
          />
        )}
        renderOption={(props, option: IPlace) => {
          // Generate a unique key using the props key which includes the index
          const uniqueKey =
            `${(props as any).key}-${Math.random()}` || `${option.description}-${Math.random()}`;

          return (
            <li {...props} key={uniqueKey} className={classes.listItem} role="listitem">
              <span className={classes.highlighted}>{option.structured_formatting.main_text}</span>
              <span className={classes.secondaryText}>
                {option.structured_formatting.secondary_text}
              </span>
            </li>
          );
        }}
      />
    );
  },
);
